import { useAuthContext } from '../../auth';
import { routes } from '../../routes';
import { Link } from 'react-router-dom';
import { RefObject, useEffect, useState } from 'react';
import { Wordmark } from './Wordmark';

type HeroProps = {
  ctaRef?: RefObject<HTMLAnchorElement>;
};

export const Hero = ({ ctaRef }: HeroProps) => {
  const { user } = useAuthContext();
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 50 && !hasScrolled) {
        setHasScrolled(true);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasScrolled]);

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center px-6 py-20 overflow-hidden">
      {/* Decorative geometric elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-600/5 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-rose-400/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      {/* Precision grid accent - top right */}
      <div className="absolute top-32 right-20 hidden lg:grid grid-cols-4 gap-2 opacity-20">
        {[...Array(16)].map((_, i) => (
          <div
            key={i}
            className="w-2 h-2 rounded-full bg-blue-600"
            style={{
              animationDelay: `${i * 0.05}s`,
              opacity: Math.random() * 0.5 + 0.3,
            }}
          />
        ))}
      </div>

      {/* Precision line accent - left */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 hidden lg:block">
        <div className="w-24 h-px bg-gradient-to-r from-transparent via-blue-600/30 to-transparent" />
        <div className="w-16 h-px bg-gradient-to-r from-transparent via-blue-600/20 to-transparent mt-3 ml-8" />
        <div className="w-20 h-px bg-gradient-to-r from-transparent via-blue-600/25 to-transparent mt-3 ml-4" />
      </div>

      <div className="max-w-5xl mx-auto text-center relative z-10">
        {/* Wordmark */}
        <div
          className="mb-16 animate-fade-in-up"
          style={{ animationDelay: '0s' }}
        >
          <Wordmark className="h-12 w-auto mx-auto" />
        </div>

        {/* Primary headline - staggered lines */}
        <h1 className="mb-8">
          <span
            className="block text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 tracking-tight animate-fade-in-up"
            style={{ animationDelay: '0.1s' }}
          >
            One template.
          </span>
          <span
            className="block text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 tracking-tight animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            Every variant.
          </span>
          <span
            className="block text-5xl sm:text-6xl lg:text-7xl font-extrabold text-blue-600 tracking-tight animate-fade-in-up"
            style={{ animationDelay: '0.3s' }}
          >
            Fix once,
          </span>
          <span
            className="block text-5xl sm:text-6xl lg:text-7xl font-extrabold text-blue-600 tracking-tight animate-fade-in-up"
            style={{ animationDelay: '0.4s' }}
          >
            Fixed everywhere.
          </span>
        </h1>

        {/* Subheadline block */}
        <div className="mb-12">
          <p
            className="text-xl sm:text-2xl text-gray-500 font-medium mb-3 animate-fade-in-up"
            style={{ animationDelay: '0.5s' }}
          >
            Fewer review cycles. Faster delivery.
          </p>
          <p
            className="text-sm sm:text-base text-gray-400 tracking-wide uppercase animate-fade-in-up"
            style={{ animationDelay: '0.6s' }}
          >
            Built for regulated email teams
          </p>
        </div>

        {/* CTA */}
        <div
          className="animate-fade-in-up"
          style={{ animationDelay: '0.7s' }}
        >
          {user ? (
            <Link
              ref={ctaRef}
              to="/orgs"
              className="inline-block bg-blue-600 text-white font-semibold text-lg px-10 py-5 rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30 hover:-translate-y-0.5"
            >
              Go to Dashboard
            </Link>
          ) : (
            <Link
              ref={ctaRef}
              to={routes.login.getPath()}
              className="inline-block bg-rose-400 text-white font-semibold text-lg px-10 py-5 rounded-xl hover:bg-rose-500 transition-all duration-300 shadow-lg shadow-rose-400/25 hover:shadow-xl hover:shadow-rose-400/30 hover:-translate-y-0.5"
            >
              Ship Emails Faster
            </Link>
          )}
        </div>
      </div>

      {/* Scroll indicator - fades after first scroll */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce transition-opacity duration-500 ${
          hasScrolled ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          opacity: 0;
          animation: fade-in-up 0.6s ease-out forwards;
        }
      `}</style>
    </section>
  );
};
